import { useEffect, useState } from "react";
import { Loader2, Table2, Info } from "lucide-react";
import { getQuoteMatrix } from "../lib/api";

const fmtUSD = (n) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const MatrixCell = ({ cell, qty, paperLabel }) => {
  const [hover, setHover] = useState(false);
  if (!cell || cell.error) {
    return (
      <td className="px-3 py-2 text-center text-[11px] font-mono text-stone-400">—</td>
    );
  }
  const margin = cell.margin || 0;
  const marginPct = cell.retail > 0 ? Math.round((margin / cell.retail) * 100) : 0;
  return (
    <td
      className="relative px-3 py-2 text-center cursor-default transition"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`matrix-cell-${qty}-${cell.paper_id}`}
      style={{
        background: hover ? "var(--ups-muted)" : "transparent",
      }}
    >
      <div className="font-mono text-[13px] font-bold tabular-nums">{fmtUSD(cell.retail)}</div>
      <div className="text-[9px] font-mono text-stone-500 mt-0.5">
        {cell.unit_retail ? `${fmtUSD(cell.unit_retail)} ea` : ""}
      </div>
      {hover && (
        <div
          className="absolute z-30 left-1/2 -translate-x-1/2 -top-2 -translate-y-full"
          style={{
            background: "var(--ups-ink)",
            color: "#FAFAF9",
            border: "2px solid #FFB500",
            borderRadius: 3,
            padding: "8px 10px",
            minWidth: 180,
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
          }}
        >
          <div className="text-[9px] font-bold uppercase tracking-[0.18em] mb-1.5" style={{ color: "#A8A29E" }}>
            {paperLabel} · {qty}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
            <span className="font-mono text-stone-400">Retail</span>
            <span className="font-mono font-bold text-right">{fmtUSD(cell.retail)}</span>
            <span className="font-mono text-stone-400">Your price</span>
            <span className="font-mono font-bold text-right" style={{ color: "#FFB500" }}>{fmtUSD(cell.ups)}</span>
            <span className="font-mono text-stone-400">Margin</span>
            <span className="font-mono font-bold text-right" style={{ color: "#22C55E" }}>{fmtUSD(margin)} <span className="text-[9px] opacity-70">({marginPct}%)</span></span>
          </div>
          <div
            className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-3 h-3 rotate-45"
            style={{ background: "var(--ups-ink)", borderRight: "2px solid #FFB500", borderBottom: "2px solid #FFB500" }}
          />
        </div>
      )}
    </td>
  );
};

const PricingMatrixGrid = ({ sessionId, productId, productName, defaultSizeId, defaultPaperId }) => {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sizeId, setSizeId] = useState(defaultSizeId || null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    setLoading(true);
    getQuoteMatrix({ sessionId, productId, sizeId })
      .then((m) => { if (!cancelled) { setMatrix(m); setError(""); } })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Could not load pricing matrix"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId, productId, sizeId]);

  if (loading && !matrix) {
    return (
      <div className="mt-5 mb-2 p-4 rounded-sm flex items-center text-[12px] text-stone-500"
        style={{ border: "1px dashed var(--ups-border)", background: "var(--ups-muted)" }}>
        <Loader2 size={14} className="animate-spin mr-2" /> Loading pricing options…
      </div>
    );
  }
  if (error || !matrix) {
    return null; // silently hide if matrix fails — main quote still shows
  }
  if (!matrix.papers?.length || !matrix.quantities?.length) return null;

  return (
    <div className="mt-2 mb-1 rounded-sm overflow-hidden" style={{ border: "1px solid var(--ups-border)", background: "#fff" }} data-testid="pricing-matrix-grid">
      <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap" style={{ background: "var(--ups-muted)", borderBottom: "1px solid var(--ups-border)" }}>
        <div>
          <div className="overline flex items-center gap-1.5"><Table2 size={12} /> {productName} · Volume Pricing</div>
          <div className="text-[11px] font-mono text-stone-500 mt-0.5 flex items-center gap-1">
            <Info size={11} /> Hover any price to see your <span className="font-bold" style={{ color: "var(--ups-accent)" }}>{matrix.discount_percent}% partner price</span> &amp; margin.
          </div>
        </div>
        {matrix.sizes && matrix.sizes.length > 1 && (
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-stone-500">Size</label>
            <select
              value={sizeId || matrix.size_id}
              onChange={(e) => setSizeId(e.target.value)}
              className="input-swiss px-2 py-1 rounded-sm text-[12px]"
              data-testid="matrix-size-select"
            >
              {matrix.sizes.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            {loading && <Loader2 size={12} className="animate-spin text-stone-400" />}
          </div>
        )}
      </div>

      <div className="overflow-auto">
        <table className="w-full text-[12px] border-collapse">
          <thead>
            <tr style={{ background: "#FAFAF9", borderBottom: "2px solid var(--ups-border)" }}>
              <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[0.14em] sticky left-0 bg-[#FAFAF9]" style={{ minWidth: 80 }}>Qty</th>
              {matrix.papers.map((p) => (
                <th key={p.id} className="px-3 py-2 text-center text-[10px] font-bold uppercase tracking-[0.14em] whitespace-nowrap"
                  style={{ background: p.id === defaultPaperId ? "#FEF3C7" : "transparent", minWidth: 110 }}>
                  {p.label}
                  {p.id === defaultPaperId && <div className="text-[8px] font-mono opacity-70 mt-0.5">(your selection)</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.rows.map((row) => (
              <tr key={row.quantity} style={{ borderBottom: "1px solid var(--ups-border)" }} data-testid={`matrix-row-${row.quantity}`}>
                <td className="px-3 py-2 font-mono text-[12px] font-bold sticky left-0 bg-white" style={{ borderRight: "1px solid var(--ups-border)" }}>
                  {row.quantity.toLocaleString()}
                </td>
                {row.cells.map((cell) => {
                  const paperLabel = matrix.papers.find((pp) => pp.id === cell.paper_id)?.label || cell.paper_id;
                  return <MatrixCell key={`${row.quantity}-${cell.paper_id}`} cell={cell} qty={row.quantity.toLocaleString()} paperLabel={paperLabel} />;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PricingMatrixGrid;
